import {
  // TableChart as CsvIcon,
  InsertDriveFile as DefaultIcon,
  // Description as DocIcon,
  // PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { Stack, styled, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localeFormatterHelper } from '../../../helpers/formatter/localeFormatter.helper';
import { AIAssistanRes } from '../service/aiAssistant.service';

export const File: FC<{ file: AIAssistanRes['reply']['content']['files'][number] }> = ({
  file,
}) => {
  const { name, expiresDate } = extractFileInfo(file!.path);
  const [clicked, setClicked] = useState(false);
  const [isExpired, setIsExpired] = useState(expiresDate < new Date());
  const { t } = useTranslation();

  useEffect(() => {
    if (!isExpired) {
      const adjustedExpiresDate = new Date(expiresDate.getTime() - 60000);
      const timeLeft = adjustedExpiresDate.getTime() - new Date().getTime();
      if (timeLeft > 0) {
        const timeout = setTimeout(() => {
          setIsExpired(true);
        }, timeLeft);
        return () => clearTimeout(timeout);
      } else {
        setIsExpired(true);
      }
    }
  }, [expiresDate, isExpired]);

  return (
    <Wrapper clicked={clicked} onClick={() => !isExpired && setClicked(true)} key={name}>
      <ResetLink
        href={isExpired ? undefined : file?.path}
        download={isExpired ? undefined : name}
        style={{ cursor: isExpired ? 'not-allowed' : 'pointer', opacity: isExpired ? 0.5 : 1 }}
      >
        <Stack direction='row' gap={'10px'} alignItems={'center'}>
          <DocumentIcon
            height='24px'
            width='24px'
            color={'rgba(244, 140, 7, 1)'}
            extension={file!.file_format}
          />
          <Typography
            sx={{
              display: 'inline-block',
              maxWidth: '100%', // allow to adapt to container
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              direction: 'rtl', // right-to-left text direction for left-side ellipsis
              textAlign: 'left', // ensures proper alignment of visible text
            }}
            fontSize={'14px'}
            fontWeight={400}
            variant='body2'
          >
            {name}
          </Typography>
        </Stack>
        <Typography fontSize={'10px'} variant='body2'>
          {isExpired
            ? t('aiAssistant.downloadExpired')
            : `${t('aiAssistant.downloadAvaliable')} ${localeFormatterHelper.formatDate(
                expiresDate,
                { dateStyle: 'short', timeStyle: 'short' },
                true,
              )}`}
        </Typography>
      </ResetLink>
    </Wrapper>
  );
};

const Wrapper = styled('div')<{ clicked: boolean }>(() => ({
  marginBottom: '16px',
  padding: '8px 16px',
  borderRadius: '10px',
  backgroundColor: 'rgba(244, 140, 7, 0.12)',
  cursor: 'pointer',
}));

const ResetLink = styled('a')`
  text-decoration: none;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
    color: inherit;
    outline: none;
  }
`;

function extractFileInfo(url: string): { name: string; expiresDate: Date } {
  const urlObj = new URL(url);

  // Extract file name from pathname
  const pathSegments = urlObj.pathname.split('/');
  const fileName = pathSegments[pathSegments.length - 1].split('?')[0];

  // Extract expires timestamp from query params
  const expiresTimestamp = urlObj.searchParams.get('Expires');
  const expiresDate = expiresTimestamp ? new Date(parseInt(expiresTimestamp) * 1000) : new Date();

  return {
    name: fileName,
    expiresDate,
  };
}

interface DocumentIconProps {
  extension: string;
  [key: string]: any;
}

const DocumentIcon = ({ extension, ...props }: DocumentIconProps) => {
  const getIcon = (ext: string) => {
    switch (ext?.toLowerCase()) {
      // case '.doc':
      // case '.docx':
      //   return <DocIcon style={{ ...props }} />;
      // case '.pdf':
      //   return <PdfIcon style={{ ...props }} />;
      // case '.csv':
      //   return <CsvIcon style={{ ...props }} />;
      // case '.txt':
      // case '.rtf':
      //   return <DefaultIcon style={{ ...props }} />;
      default:
        return <DefaultIcon style={{ ...props }} />;
    }
  };

  return getIcon(extension);
};
